import * as React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Table from "@material-ui/core/Table/Table";
import TableHead from "@material-ui/core/TableHead/TableHead";
import TableRow from "@material-ui/core/TableRow/TableRow";
import TableCell from "@material-ui/core/TableCell/TableCell";
import TableBody from "@material-ui/core/TableBody/TableBody";
import TableSortLabel from "@material-ui/core/TableSortLabel/TableSortLabel";
import withStyles from "@material-ui/core/styles/withStyles";
import CurrencyNumber from "../UI/CurrencyNumber";
import PercentageNumber from "../UI/PercentageNumber";
import { portfolioAction } from "../../actions/portfolio.action";
import { sortPortfolioOutput, stableSort } from "../../utilities/sorting";
import HighLevelDisclosure from "../../containers/SummaryPDF/components/HighLevelDisclosure";
import { isPremiumView } from "../../utilities/access";
import {convertPerformanceType} from "../../utilities/cpr";

const styles = (theme) => ({
  summaryRow: {
    background: theme.palette.grey[200],
    "-webkit-print-color-adjust": "exact", // PDF/Print styling
  },
  tableCell: {
    padding: 2,
    fontSize: 12,
    "@media print": {
      fontSize: 11,
      "& .MuiTableSortLabel-icon": {
        display: "none",
      },
    },
    [theme.breakpoints.down("lg")]: {
      lineHeight: 1.2,
      fontSize: 12
    },
    "&.header": {
      fontSize: 13,
      wordWrap: "break-word",
      fontWeight: 500,
      color: theme.palette.primary.main,
      lineHeight: 1.6,
      "@media print": {
        fontSize: 11,
      },
      [theme.breakpoints.down("lg")]: {
        lineHeight: 1.2,
        fontSize: 11,
        paddingBottom: 20
      },
      '& .MuiTableSortLabel-icon': {
        [theme.breakpoints.down("lg")]: {
          position: 'absolute',
          bottom: '-16px',
          fontSize: '14px',
          textAlign: 'center',
          margin: 0,
          padding: 0,
        },
      }
    },
    "&.summary-cell": {
      fontWeight: 500,
      padding: "15px 2px",
      borderBottom: "none",
      height: 24,
      lineHeight: 2,
      fontSize: 14,
      [theme.breakpoints.down("lg")]: {
        fontSize: 12
      },
    },
    "&.wrap-cell": {
      maxWidth: 120,
      wordWrap: "break-word",
    },
  },
  highlight: {
    height: 80,
    width: 80,
    border: `1px solid ${theme.palette.common.black}`,
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "10px 0 10px 10px",
    textAlign: "right",
    float: "right",
  },
});

const tableHead = {
  manager: {
    id: "managerDetail.Name",
    numeric: false,
    label: "Fund or Investment Strategy",
  },
  type: {
    id: "managerDetail.Type",
    numeric: false,
    label: "Investment Type",
  },
  category: {
    id: "managerDetail.GlobalCategory",
    numeric: false,
    label: "Asset<br />Category",
  },
  weight: {
    id: "managerWeight",
    numeric: true,
    label: "Weight",
  },
  upCapture: {
    id: "managerDetail.UpCapture",
    numeric: true,
    label: "Up<br/>Capture",
  },
  downCapture: {
    id: "managerDetail.DownCapture",
    numeric: true,
    label: "Down<br/>Capture",
  },
  drawdown: {
    id: "managerDetail.MaxDrawdown",
    numeric: true,
    label: "Worst<br/>Drawdown",
  },
  worst4qReturn: {
    id: "managerDetail.Worst4QtrReturn",
    numeric: true,
    label: `Worst Four<br/>Quarters<br/>Return`,
  },
  worstYearReturn: {
    id: "managerDetail.WorstCalendarYearReturn",
    numeric: true,
    label: "Worst<br/>Calendar<br/>Yr Return",
  },
  annualizedReturn: {
    id: "managerDetail.AnnualizedReturn",
    numeric: true,
    label: "Annualized<br/>Return",
  },
  portfolioYield: {
    id: "managerDetail.Implementation12MonthYield",
    numeric: true,
    label: "Portfolio<br/>Yield",
  },
  finalWealth: {
    id: "managerFinalWealth",
    numeric: true,
    label: "Ending<br/>Capital",
  },
  finalWealthWithAnnualWithdrawal: {
    id: "managerFinalWealthWithAnnualWithdrawal",
    numeric: true,
    label: "Ending Capital<br/>with Withdrawals",
  },
};

/*
  About this component:
    This component is basically same as CyPortfolioDetails component 
    which displays the manager details on the table, but has following differences:
    1) Table head is clickable and sortable by asc / desc
    2) The sorting order (asc / desc) and sort by id are saved in the store
       so that the table which uses same component displays same order of data
*/
class CyPortfolioDetailsSortable extends React.Component {
  /* Dispatch an action to save new order and orderBy id */
  handleRequestSort = (event, property) => {
    const { orderBy, order } = this.props.sortedPortfolioOrders;
    let newOrder = "desc";

    if (orderBy === property && order === "desc") {
      newOrder = "asc";
    }
    this.props.dispatch(
      portfolioAction.saveSortedPortfolioOrders({
        order: newOrder,
        orderBy: property,
      })
    );
  };

  /* On click func triggered by table head - gets orderBy id in param */
  createSortHandler = (property) => (event) => {
    this.handleRequestSort(event, property);
  };

  /* Returns table head row */
  returnMainTableHeadRow = (
    order,
    orderBy,
    riskPeriod,
    finalWealthWithAnnualWithdrawal
  ) => {
    const { classes } = this.props;
    const columnCount = finalWealthWithAnnualWithdrawal ? 13 : 11;
    return (
      <TableRow>
        <TableCell
          key={tableHead.manager.id}
          align={tableHead.manager.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={orderBy === tableHead.manager.id ? order : false}
          style={{ width: `${(100 / columnCount) * 2}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.manager.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.manager.id)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: tableHead.manager.label }}
            />
          </TableSortLabel>
        </TableCell>

        <TableCell
          align={'left'}
          className={`${classes.tableCell} header`}
          style={{width: `${100 / columnCount}%`}}
        >
          <span>Ticker</span>
        </TableCell>

        <TableCell
          align={'left'}
          className={`${classes.tableCell} header`}
          style={{width: `${(100 / columnCount) * 1.25}%`}}
        >
          <span>Performance Type</span>
        </TableCell>

        <TableCell
          key={tableHead.category.id}
          align={tableHead.category.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={orderBy === tableHead.category.id ? order : false}
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.category.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.category.id)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: tableHead.category.label }}
            />
          </TableSortLabel>
        </TableCell>

        <TableCell
          key={tableHead.weight.id}
          align={tableHead.weight.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={orderBy === tableHead.weight.id ? order : false}
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.weight.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.weight.id)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: tableHead.weight.label }}
            />
          </TableSortLabel>
        </TableCell>

        <TableCell
          align={'right'}
          className={`${classes.tableCell} header`}
          style={{width: `${100 / columnCount}%`}}
        >
          <span>Expenses</span>
        </TableCell>

        <TableCell
          key={tableHead.upCapture.id}
          align={tableHead.upCapture.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={orderBy === tableHead.upCapture.id ? order : false}
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.upCapture.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.upCapture.id)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: tableHead.upCapture.label }}
            />
          </TableSortLabel>
        </TableCell>
        <TableCell
          key={tableHead.downCapture.id}
          align={tableHead.downCapture.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={orderBy === tableHead.downCapture.id ? order : false}
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.downCapture.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.downCapture.id)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: tableHead.downCapture.label }}
            />
          </TableSortLabel>
        </TableCell>
        {riskPeriod === "mdd" && (
          <TableCell
            key={tableHead.drawdown.id}
            align={tableHead.drawdown.numeric ? "right" : "left"}
            className={`${classes.tableCell} header`}
            sortDirection={orderBy === tableHead.drawdown.id ? order : false}
            style={{ width: `${100 / columnCount}%` }}
          >
            <TableSortLabel
              active={orderBy === tableHead.drawdown.id}
              direction={order}
              onClick={this.createSortHandler(tableHead.drawdown.id)}
            >
              <span
                dangerouslySetInnerHTML={{ __html: tableHead.drawdown.label }}
              />
            </TableSortLabel>
          </TableCell>
        )}
        {riskPeriod === "worst.4q" && (
          <TableCell
            key={tableHead.worst4qReturn.id}
            align={tableHead.worst4qReturn.numeric ? "right" : "left"}
            className={`${classes.tableCell} header`}
            sortDirection={
              orderBy === tableHead.worst4qReturn.id ? order : false
            }
            style={{ width: `${100 / columnCount}%` }}
          >
            <TableSortLabel
              active={orderBy === tableHead.worst4qReturn.id}
              direction={order}
              onClick={this.createSortHandler(tableHead.worst4qReturn.id)}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: tableHead.worst4qReturn.label,
                }}
              />
            </TableSortLabel>
          </TableCell>
        )}
        {riskPeriod === "worst.calendar.year" && (
          <TableCell
            key={tableHead.worstYearReturn.id}
            align={tableHead.worstYearReturn.numeric ? "right" : "left"}
            className={`${classes.tableCell} header`}
            sortDirection={
              orderBy === tableHead.worstYearReturn.id ? order : false
            }
            style={{ width: `${100 / columnCount}%` }}
          >
            <TableSortLabel
              active={orderBy === tableHead.worstYearReturn.id}
              direction={order}
              onClick={this.createSortHandler(tableHead.worstYearReturn.id)}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: tableHead.worstYearReturn.label,
                }}
              />
            </TableSortLabel>
          </TableCell>
        )}
        <TableCell
          key={tableHead.annualizedReturn.id}
          align={tableHead.annualizedReturn.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={
            orderBy === tableHead.annualizedReturn.id ? order : false
          }
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.annualizedReturn.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.annualizedReturn.id)}
            style={{ width: `${100 / columnCount}%` }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: tableHead.annualizedReturn.label,
              }}
            />
          </TableSortLabel>
        </TableCell>
        <TableCell
          key={tableHead.portfolioYield.id}
          align={tableHead.portfolioYield.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={
            orderBy === tableHead.portfolioYield.id ? order : false
          }
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.portfolioYield.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.portfolioYield.id)}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: tableHead.portfolioYield.label,
              }}
            />
          </TableSortLabel>
        </TableCell>
        <TableCell
          key={tableHead.finalWealth.id}
          align={tableHead.finalWealth.numeric ? "right" : "left"}
          className={`${classes.tableCell} header`}
          sortDirection={orderBy === tableHead.finalWealth.id ? order : false}
          style={{ width: `${100 / columnCount}%` }}
        >
          <TableSortLabel
            active={orderBy === tableHead.finalWealth.id}
            direction={order}
            onClick={this.createSortHandler(tableHead.finalWealth.id)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: tableHead.finalWealth.label }}
            />
          </TableSortLabel>
        </TableCell>
        {finalWealthWithAnnualWithdrawal && (
          <TableCell
            key={tableHead.finalWealthWithAnnualWithdrawal.id}
            align={
              tableHead.finalWealthWithAnnualWithdrawal.numeric
                ? "right"
                : "left"
            }
            className={`${classes.tableCell} header`}
            sortDirection={
              orderBy === tableHead.finalWealthWithAnnualWithdrawal.id
                ? order
                : false
            }
            style={{ width: `${100 / columnCount}%` }}
          >
            <TableSortLabel
              active={orderBy === tableHead.finalWealthWithAnnualWithdrawal.id}
              direction={order}
              onClick={this.createSortHandler(
                tableHead.finalWealthWithAnnualWithdrawal.id
              )}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: tableHead.finalWealthWithAnnualWithdrawal.label,
                }}
              />
            </TableSortLabel>
          </TableCell>
        )}
        {
          // finalWealthWithAnnualWithdrawal &&
          // <TableCell
          //   align={'right'}
          //   className={`${classes.tableCell} header`}
          //   style={{width: `${100 / columnCount}%`}}
          // >
          //   <span>Total<br />Withdrawals</span>
          // </TableCell>
        }
      </TableRow>
    );
  };

  /* Returns table body row */
  returnMainTableBodyRow = (
    manager,
    id,
    riskPeriod,
    numberOfManagers = undefined
  ) => {
    const { classes } = this.props;

    return (
      <TableRow key={id} style={numberOfManagers === 1 ? { height: 200 } : {}}>
        <TableCell className={`${classes.tableCell} wrap-cell`}>
          {manager.managerDetail.ImplementationName}
        </TableCell>
        <TableCell className={`${classes.tableCell} wrap-cell`}>
          {manager.managerDetail.ImplementationTicker || "--"}
        </TableCell>
        <TableCell className={`${classes.tableCell} wrap-cell`}>
          { convertPerformanceType(manager.managerDetail) }
        </TableCell>
        <TableCell className={`${classes.tableCell} wrap-cell`}>
          {manager.managerDetail.GlobalCategory}
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <PercentageNumber value={manager.managerWeight} />
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          {
            !manager.managerDetail.ImplementationExpenseRatio ||
            manager.managerDetail.ImplementationExpenseRatio === "" ||
            manager.managerDetail.ImplementationExpenseRatio === " "?
              "--" :
              parseFloat(manager.managerDetail.ImplementationExpenseRatio).toFixed(2) + '%'
          }
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <PercentageNumber value={manager.managerDetail.UpCapture} />
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <PercentageNumber value={manager.managerDetail.DownCapture} />
        </TableCell>
        {riskPeriod === "mdd" && (
          <TableCell align="right" className={classes.tableCell}>
            <PercentageNumber value={manager.managerDetail.MaxDrawdown} />
          </TableCell>
        )}
        {riskPeriod === "worst.4q" && (
          <TableCell align="right" className={classes.tableCell}>
            <PercentageNumber value={manager.managerDetail.Worst4QtrReturn} />
          </TableCell>
        )}
        {riskPeriod === "worst.calendar.year" && (
          <TableCell align="right" className={classes.tableCell}>
            <PercentageNumber
              value={manager.managerDetail.WorstCalendarYearReturn}
            />
          </TableCell>
        )}
        <TableCell align="right" className={classes.tableCell}>
          <PercentageNumber value={manager.managerDetail.AnnualizedReturn} />
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <PercentageNumber
            value={manager.managerDetail.Implementation12MonthYield}
            needCalculation={false}
          />
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <CurrencyNumber value={manager.managerFinalWealth} />
        </TableCell>
        {manager.managerFinalWealthWithAnnualWithdrawal >= 0 && (
          <TableCell align="right" className={classes.tableCell}>
            <CurrencyNumber
              value={manager.managerFinalWealthWithAnnualWithdrawal}
            />
          </TableCell>
        )}
        {
          // manager.managerFinalWealthWithAnnualWithdrawal >= 0 &&
          // <TableCell align="right" className={classes.tableCell}>--</TableCell>
        }
      </TableRow>
    );
  };

  /* Returns the summary row of portfolio details */
  returnPortfolioSummaryRow = (
    portfolioSummary,
    finalWealth,
    managerList,
    riskPeriod,
    finalWealthWithAnnualWithdrawal,
    totalWithdrawal
  ) => {
    const { classes, bdIsCambridge, accessLevel, forPrinting } = this.props;
    const hiddenCell = (bdIsCambridge || !isPremiumView(accessLevel)) && forPrinting;
    const managerFee = managerList.reduce((accu, manager) =>
        (accu + manager.managerWeight * Number(manager.managerDetail.ImplementationExpenseRatio || 0))
      , 0);

    return (
      <TableRow className={classes.summaryRow}>
        <TableCell colSpan={4} className={`${classes.tableCell} summary-cell`}>
          Cy Optimized Portfolio <sup>*</sup>
          <br />
          Benchmark ({portfolioSummary.benchmark.Name}) <sup>**</sup>
          <br />
          {/*Difference*/}
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          100%
          <br />
          <br />
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          <PercentageNumber value={managerFee} needCalculation={false} />
          <br />
          <br />
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          {
            !hiddenCell &&
            <PercentageNumber
              value={portfolioSummary.recommendationSummary.UpCapture}
            />
          }
          <br />
          <PercentageNumber value={portfolioSummary.benchmark.UpCapture} />
          <br />
          {/*<PercentageNumber value={portfolioSummary.recommendationSummary.UpCapture - portfolioSummary.benchmark.UpCapture} showSign={true}/>*/}
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          {
            !hiddenCell &&
            <PercentageNumber
              value={portfolioSummary.recommendationSummary.DownCapture}
            />
          }
          <br />
          <PercentageNumber value={portfolioSummary.benchmark.DownCapture} />
          <br />
          {/*<PercentageNumber value={portfolioSummary.recommendationSummary.DownCapture - portfolioSummary.benchmark.DownCapture} showSign={true}/>*/}
        </TableCell>

        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          {riskPeriod === "mdd" && (
            <div>
              {
                !hiddenCell &&
                <PercentageNumber
                  value={portfolioSummary.recommendationSummary.MaxDrawdown}
                />
              }
              <br />
              <PercentageNumber
                value={portfolioSummary.benchmark.MaxDrawdown}
              />
              <br />
              {/*<PercentageNumber value={portfolioSummary.recommendationSummary.MaxDrawdown - portfolioSummary.benchmark.MaxDrawdown} showSign={true}/>*/}
            </div>
          )}
          {riskPeriod === "worst.4q" && (
            <div>
              {
                !hiddenCell &&
                <PercentageNumber
                  value={portfolioSummary.recommendationSummary.Worst4QtrReturn}
                />
              }
              <br />
              <PercentageNumber
                value={portfolioSummary.benchmark.Worst4QtrReturn}
              />
              <br />
              {/*<PercentageNumber value={portfolioSummary.recommendationSummary.Worst4QtrReturn - portfolioSummary.benchmark.Worst4QtrReturn} showSign={true}/>*/}
            </div>
          )}
          {riskPeriod === "worst.calendar.year" && (
            <div>
              {
                !hiddenCell &&
                <PercentageNumber
                  value={
                    portfolioSummary.recommendationSummary.WorstCalendarYearReturn
                  }
                />
              }
              <br />
              <PercentageNumber
                value={portfolioSummary.benchmark.WorstCalendarYearReturn}
              />
              <br />
              {/*<PercentageNumber value={portfolioSummary.recommendationSummary.WorstCalendarYearReturn - portfolioSummary.benchmark.WorstCalendarYearReturn} showSign={true}/>*/}
            </div>
          )}
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          <div>
            {
              !hiddenCell &&
              <PercentageNumber
                value={portfolioSummary.recommendationSummary.AnnualizedReturn}
              />
            }
            <br />
            <PercentageNumber
              value={portfolioSummary.benchmark.AnnualizedReturn}
            />
            <br />
            {/*<PercentageNumber value={portfolioSummary.recommendationSummary.AnnualizedReturn - portfolioSummary.benchmark.AnnualizedReturn} showSign={true}/>*/}
          </div>
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          <div>
            {
              !hiddenCell &&
              <PercentageNumber
                value={
                  portfolioSummary.recommendationSummary
                    .Implementation12MonthYield
                }
                needCalculation={false}
              />
            }
            <br />
            <PercentageNumber
              value={portfolioSummary.benchmark.Implementation12MonthYield}
              needCalculation={false}
            />
            <br />
            {/*<PercentageNumber value={portfolioSummary.recommendationSummary.Implementation12MonthYield - portfolioSummary.benchmark.Implementation12MonthYield} needCalculation={false} showSign={true}/>*/}
          </div>
        </TableCell>
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        >
          <div>
            {
              !hiddenCell &&
              <CurrencyNumber value={finalWealth["Cy"]}/>
            }
            <br />
            <CurrencyNumber value={finalWealth["Benchmark"]} />
            <br />
            {/*<CurrencyNumber value={finalWealth['Cy'] - finalWealth['Benchmark']} showSign={true}/>*/}
          </div>
        </TableCell>
        {finalWealthWithAnnualWithdrawal && (
          <TableCell
            align="right"
            className={`${classes.tableCell} summary-cell`}
          >
            <div>
              {
                !hiddenCell &&
                <CurrencyNumber value={finalWealthWithAnnualWithdrawal["Cy"]}/>
              }
              <br />
              <CurrencyNumber
                value={finalWealthWithAnnualWithdrawal["Benchmark"]}
              />
              <br />
              {/*<CurrencyNumber value={finalWealthWithAnnualWithdrawal['Cy'] - finalWealthWithAnnualWithdrawal['Benchmark']} showSign={true}/>*/}
            </div>
          </TableCell>
        )}
        <TableCell
          align="right"
          className={`${classes.tableCell} summary-cell`}
        />
        {
          // finalWealthWithAnnualWithdrawal &&
          // <TableCell align="right" className={`${classes.tableCell} summary-cell totalwith`}>
          //   <div>
          //     <CurrencyNumber value={totalWithdrawal['Cy']} /><br />
          //     <CurrencyNumber value={totalWithdrawal['Benchmark']} /><br />
          //     <CurrencyNumber value={totalWithdrawal['Cy'] - totalWithdrawal['Benchmark']} showSign={true}/>
          //   </div>
          // </TableCell>
        }
      </TableRow>
    );
  };

  render() {
    const {
      portfolioSummary,
      finalWealth,
      riskPeriod,
      finalWealthWithAnnualWithdrawal,
      totalWithdrawal,
    } = this.props;
    const { orderBy, order } = this.props.sortedPortfolioOrders;
    let managerList = [...this.props.managerList];
    let isSingleStrategy = managerList.length === 1;
    if (isSingleStrategy) {
      managerList[0].managerFinalWealth = finalWealth["Cy"];
    }
    if (finalWealthWithAnnualWithdrawal) {
      managerList.forEach(
        (m) =>
          (m.managerFinalWealthWithAnnualWithdrawal =
            finalWealthWithAnnualWithdrawal["Cy"] * m.managerWeight)
      );
    }

    return (
      <React.Fragment>
        <Table style={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            {this.returnMainTableHeadRow(
              order,
              orderBy,
              riskPeriod,
              finalWealthWithAnnualWithdrawal
            )}
          </TableHead>
          <TableBody>
            {stableSort(managerList, sortPortfolioOutput(order, orderBy))
              .filter((manager) => manager.managerCode !== "USD Cash")
              .map((manager, i) =>
                this.returnMainTableBodyRow(
                  manager,
                  i,
                  riskPeriod,
                  managerList.length
                )
              )}
            {managerList
              .filter((manager) => manager.managerCode === "USD Cash")
              .map((manager, i) =>
                this.returnMainTableBodyRow(manager, i, riskPeriod)
              )}
            {this.returnPortfolioSummaryRow(
              portfolioSummary,
              finalWealth,
              managerList,
              riskPeriod,
              finalWealthWithAnnualWithdrawal,
              totalWithdrawal
            )}
          </TableBody>
        </Table>

        <HighLevelDisclosure showClause={[1, 2, 3]} />
      </React.Fragment>
    );
  }
}

CyPortfolioDetailsSortable.propTypes = {
  classes: PropTypes.object.isRequired,
  managerList: PropTypes.arrayOf(
    PropTypes.shape({
      managerCode: PropTypes.string.isRequired,
      managerWeight: PropTypes.number.isRequired,
      managerFinalWealth: PropTypes.number.isRequired,
      managerDetail: PropTypes.shape({
        Code: PropTypes.string,
        Name: PropTypes.string.isRequired,
        GlobalCategory: PropTypes.string,
        Type: PropTypes.string.isRequired,
        UpCapture: PropTypes.string.isRequired,
        DownCapture: PropTypes.string.isRequired,
        Worst4QtrReturn: PropTypes.string.isRequired,
        AnnualizedReturn: PropTypes.string.isRequired,
        MaxDrawdown: PropTypes.string.isRequired,
        WorstCalendarYearReturn: PropTypes.string.isRequired,
        Implementation12MonthYield: PropTypes.string.isRequired,
        ImplementationName: PropTypes.string.isRequired,
        AverageYields: PropTypes.string.isRequired,
      }),
    })
  ).isRequired,
  portfolioSummary: PropTypes.shape({
    recommendationSummary: PropTypes.object,
    benchmark: PropTypes.object,
  }).isRequired,
  finalWealth: PropTypes.object.isRequired,
  finalWealthWithAnnualWithdrawal: PropTypes.object,
  riskPeriod: PropTypes.string.isRequired,
  sortedPortfolioOrders: PropTypes.object.isRequired,
  bdIsCambridge: PropTypes.bool,
  accessLevel: PropTypes.bool,
  forPrinting: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    sortedPortfolioOrders: state.portfolio.sortedPortfolioOrders,
    appName: state.app.appName,
  };
};

export default withStyles(styles)(
  connect(mapStateToProps)(CyPortfolioDetailsSortable)
);



// WEBPACK FOOTER //
// ./src/components/Charts/CyPortfolioDetailsSortable.jsx